// @ts-nocheck
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatCurrency } from "@/utils";
import {
  getPaymentStatusColor,
  getPaymentStatusLabel,
} from "@/services/Api/bankSlipApiService";

export function safe<T>(
  value: T | null | undefined,
  fallback: string = "—"
): string {
  if (value === null || value === undefined) return fallback;
  const str = String(value);
  return str.trim().length > 0 ? str : fallback;
}

export function formatDateTime(value?: string | Date | null): string {
  if (!value) return "—";
  try {
    const d = value instanceof Date ? value : new Date(value);
    return format(d, "dd MMMM yyyy, HH:mm:ss", { locale: ptBR });
  } catch {
    return "—";
  }
}

export function maskDocument(value?: string | null): string {
  if (!value) return "—";

  const strValue = String(value);

  // Se já contém asteriscos ou pontos, assumimos que já está mascarado pela API
  if (strValue.includes("*") || strValue.includes("•")) {
    return strValue; // Retorna exatamente como recebido da API
  }

  // Caso contrário, aplica máscara local (backward compatibility)
  const digits = strValue.replace(/\D/g, "");

  // Somente CPF/CNPJ; outros formatos não são documentos
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "***.$2.$3-***");
  }
  if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "**.$2.$3/$4-**"
    );
  }
  return "—";
}

export function formatMoneyBRL(amount?: number | string | null): string {
  if (
    amount === null ||
    amount === undefined ||
    amount === 0 ||
    Number.isNaN(Number(amount))
  ) {
    return "—";
  }
  return formatCurrency({ value: Number(amount) });
}

export function getBankNameByIspb(ispb?: string): string {
  if (!ispb) return "—";

  // Mapeamento dos principais códigos ISPB para nomes de bancos
  const ispbMap: Record<string, string> = {
    "00000000": "Banco do Brasil S.A.",
    "00038121": "Banco Central do Brasil - Selic",
    "00038166": "Banco Central do Brasil",
    "00250699": "AGK Corretora de Câmbio S.A.",
    "00315557": "Confederação Nacional das Cooperativas Centrais Unicred Ltda.",
    "00360305": "Caixa Econômica Federal",
    "00394460": "Secretaria do Tesouro Nacional - STN",
    "00416968": "Banco Inter S.A.",
    "00517645": "Banco Ribeirão Preto S.A.",
    "00556603": "Banco Bari de Investimentos e Financiamentos S.A.",
    "00558456": "Banco Cetelem S.A.",
    "00795423": "Banco Semear S.A.",
    "00806535": "Planner Corretora de Valores S.A.",
    "00997185": "Banco B3 S.A.",
    "01023570": "Banco Rabobank International Brasil S.A.",
    "01027058": "Cielo S.A.",
    "01073966":
      "Cooperativa de Crédito Rural de Abelardo Luz - Sulcredi/Crediluz",
    "01181521": "Banco Cooperativo Sicredi S.A.",
    "01330387":
      "Cooperativa de Crédito Rural de Pequenos Agricultores e da Reforma Agrária do CE",
    "01522368": "Banco BNP Paribas Brasil S.A.",
    "01634601":
      "Central de Cooperativas de Economia e Crédito Mútuo do Estado do RS",
    "01658426": "Cooperforte - Cooperativa de Economia e Crédito Mútuo",
    "01701201": "Kirton Bank S.A. - Banco Múltiplo",
    "01800019": "Portocred S.A. - Crédito, Financiamento e Investimento",
    "01852137": "BBC Leasing S.A. - Arrendamento Mercantil",
    "02038232": "Banco Cooperativo do Brasil S.A. - Bancoob",
    "02276653":
      "Trinus Capital Distribuidora de Títulos e Valores Mobiliários S.A.",
    "02318507": "Banco KEB Hana do Brasil S.A.",
    "02332886":
      "XP Investimentos Corretora de Câmbio, Títulos e Valores Mobiliários S/A",
    "02398976": "Uniprime Norte do Paraná - Cooperativa de Crédito Ltda",
    "02685483":
      "CM Capital Markets Corretora de Câmbio, Títulos e Valores Mobiliários Ltda",
    "02801938": "Banco Morgan Stanley S.A.",
    "02819125":
      "UBS Brasil Corretora de Câmbio, Títulos e Valores Mobiliários S.A.",
    "02992317": "Treviso Corretora de Câmbio S.A.",
    "02992335": "Câmara Interbancária de Pagamentos - CIP - LDL",
    "03012230": "Hipercard Banco Múltiplo S.A.",
    "03017677": "Banco J. Safra S.A.",
    "03046391":
      "Uniprime Central - Central Interestadual de Cooperativas de Crédito Ltda.",
    "03215790": "Banco Toyota do Brasil S.A.",
    "03311443": "Parati - Crédito, Financiamento e Investimento S.A.",
    "03323840": "Banco Alfa S.A.",
    "03502968": "PI Distribuidora de Títulos e Valores Mobiliários S.A.",
    "03532415": "Banco ABN Amro S.A.",
    "03609817": "Banco Cargill S.A.",
    "03751794":
      "Terra Investimentos Distribuidora de Títulos e Valores Mobiliários Ltda.",
    "03973814":
      "Servicoop - Cooperativa de Crédito dos Servidores Públicos Estaduais do RGS",
    "04062902": "Vision S.A. Corretora de Câmbio",
    "04184779": "Banco Bradescard S.A.",
    "04257795": "Nova Futura Corretora de Títulos e Valores Mobiliários Ltda.",
    "04307598": "Fidúcia Sociedade de Crédito ao Microempreendedor",
    "04332281": "Goldman Sachs do Brasil Banco Múltiplo S.A.",
    "04391007": "Câmara Interbancária de Pagamentos",
    "04632856": "Credisis - Central de Cooperativas de Crédito Ltda.",
    "04715685":
      "Cooperativa de Crédito Mútuo dos Despachantes de Trânsito de SC e RO",
    "04814563": "Sorocred Crédito, Financiamento e Investimento S.A.",
    "04866275": "Banco Inbursa S.A.",
    "04902979": "Banco da Amazônia S.A.",
    "04913129": "Confidence Corretora de Câmbio S.A.",
    "04913711": "Banco do Estado do Pará S.A.",
    "05351887": "Zema Crédito, Financiamento e Investimento S/A",
    "05442029":
      "Casa do Crédito S.A. Sociedade de Crédito ao Microempreendedor",
    "05463212": "Cooperativa Central de Crédito - Ailos",
    "05790149":
      "Central Cooperativa de Crédito no Estado do Espírito Santo - Cecoop",
    "06271464": "Banco Bradesco BBI S.A.",
    "07207996": "Banco Bradesco Financiamentos S.A.",
    "07237373": "Banco do Nordeste do Brasil S.A.",
    "07450604": "China Construction Bank (Brasil) Banco Múltiplo S/A",
    "07512441": "HS Financeira S/A Crédito, Financiamento e Investimentos",
    "07652226": "Lecca Crédito, Financiamento e Investimento S/A",
    "07656500": "Banco KDB do Brasil S.A.",
    "07679404": "Banco Topázio S.A.",
    "07853842": "Cooperativa de Crédito Rural de Ouro Sulcredi/Ouro",
    "07945233": "Pólocred Sociedade de Crédito ao Microempreendedor",
    "08240446": "Cooperativa de Crédito Rural de Ibiam - Sulcredi/Ibiam",
    "08253539":
      "Cooperativa de Crédito Rural de São Miguel do Oeste - Sulcredi/São Miguel",
    "08357240": "Banco CSF S.A.",
    "08561701": "Pagseguro Internet S.A.",
    "08609934": "Moneycorp Banco de Câmbio S.A.",
    "09089356": "Gerencianet S.A.",
    "09105360":
      "ICAP do Brasil Corretora de Títulos e Valores Mobiliários Ltda.",
    "09210106": "Socred S.A. - Sociedade de Crédito ao Microempreendedor",
    "09274232": "State Street Brasil S.A. - Banco Comercial",
    "09313766":
      "Caruana S.A. - Sociedade de Crédito, Financiamento e Investimento",
    "09512542": "Codepe Corretora de Valores e Câmbio S.A.",
    "09516419": "Banco Original do Agronegócio S.A.",
    "09554480": "Super Pagamentos e Administração de Meios Eletrônicos S.A.",
    "10264663": "BancoSeguro S.A.",
    "10398952": "Confederação Nacional das Cooperativas Centrais de Crédito",
    "10573521": "Mercadopago.com Representações Ltda.",
    "10664513": "Banco Agibank S.A.",
    "10690848": "Banco da China Brasil S.A.",
    "10853017": "Get Money Corretora de Câmbio S.A.",
    "10866788": "Banco Bandepe S.A.",
    "11165756": "Global Finanças Sociedade de Crédito ao Microempreendedor",
    "11476673": "Banco Randon S.A.",
    "11495073": "OM Distribuidora de Títulos e Valores Mobiliários Ltda",
    "11581339": "Money Plus Sociedade de Crédito ao Microempreendedor",
    "11703662": "Travelex Banco de Câmbio S.A.",
    "11758741": "Banco Finaxis S.A.",
    "11970623": "Senff S.A. - Crédito, Financiamento e Investimento",
    "12865507": "BRK S.A. Crédito, Financiamento e Investimento",
    "13009717": "Banco do Estado de Sergipe S.A.",
    "13059145": "Bexs Banco de Câmbio S/A",
    "13140088": "Acesso Soluções de Pagamento S.A.",
    "13220493": "BR Partners Banco de Investimento S.A.",
    "13293225": "Órama Distribuidora de Títulos e Valores Mobiliários S.A.",
    "13370835": "BPP Instituição de Pagamento S.A.",
    "13486793": "BRL Trust Distribuidora de Títulos e Valores Mobiliários S.A.",
    "13673855":
      "Fram Capital Distribuidora de Títulos e Valores Mobiliários S.A.",
    "13720915": "Banco Western Union do Brasil S.A.",
    "13884775": "Hub Pagamentos S.A",
    "14190547": "Cambionet Corretora de Câmbio Ltda.",
    "14388334": "Paraná Banco S.A.",
    "14511781": "Bari Companhia Hipotecária",
    "15114366": "Banco Bocom BBM S.A.",
    "15173776": "Banco Capital S.A.",
    "15357060": "Banco Woori Bank do Brasil S.A.",
    "15581638": "Facta Financeira S.A. - Crédito Financiamento e Investimento",
    "16501555": "Stone Pagamentos S.A.",
    "16927221": "Amazônia Corretora de Câmbio Ltda.",
    "16944141": "Broker Brasil Corretora de Câmbio Ltda.",
    "17184037": "Banco Mercantil do Brasil S.A.",
    "17298092": "Banco Itaú BBA S.A.",
    "17351180": "Banco Triângulo S.A.",
    "17352220": "Senso Corretora de Câmbio e Valores Mobiliários S.A",
    "17453575": "ICBC do Brasil Banco Múltiplo S.A.",
    "17772370": "Vip's Corretora de Câmbio Ltda.",
    "17826860": "MS Sociedade de Crédito ao Microempreendedor",
    "18188384": "Crefaz Sociedade de Crédito ao Microempreendedor",
    "18236120": "Nu Pagamentos S.A. (Nubank)",
    "18520834": "UBS Brasil Banco de Investimento S.A.",
    "19307785": "MS Bank S.A. Banco de Câmbio",
    "20155248": "Parmetal Distribuidora de Títulos e Valores Mobiliários Ltda",
    "21018182": "Boletobancário.com Tecnologia de Pagamentos Ltda.",
    "22896431": "PicPay Serviços S.A.",
    "26563270": "PicPay Serviços S.A.",
    "30723886": "Banco Modal S.A.",
    "31680151": "SWAP",
    "31872495": "C6 Bank S.A.",
    "31880826": "Banco Guanabara S.A.",
    "31895683": "Banco Industrial do Brasil S.A.",
    "32402502": "Modal S.A.",
    "59588111": "Banco Neon S.A.",
    "60701190": "Itaú Unibanco S.A.",
    "60746948": "Banco Bradesco S.A.",
    "90400888": "Banco Santander (Brasil) S.A.",
  };

  return ispbMap[ispb] || `Banco (ISPB: ${ispb})`;
}

export function getStatusPresentation(status?: string): {
  label: string;
  color: string;
} {
  try {
    const label = getPaymentStatusLabel(status || "");
    const color = getPaymentStatusColor(status || "");

    // Fallback local se as funções não retornarem valores válidos
    if (!label || label === status) {
      const localStatus = getLocalStatusPresentation(status);
      return {
        label: localStatus.label || status || "—",
        color: localStatus.color,
      };
    }

    return { label: label || status || "—", color };
  } catch (error) {
    console.warn("Error getting status presentation:", error);
    const localStatus = getLocalStatusPresentation(status);
    return {
      label: localStatus.label || status || "—",
      color: localStatus.color,
    };
  }
}

function getLocalStatusPresentation(status?: string): {
  label: string;
  color: string;
} {
  const map: Record<string, { label: string; color: string }> = {
    completed: { label: "Concluído", color: "#fae9ee" },
    paid: { label: "Concluído", color: "#fae9ee" },
    scheduled: { label: "Agendado", color: "yellow" },
    pending_approval: { label: "Pendente de Aprovação", color: "orange" },
    approved: { label: "Aprovado", color: "blue" },
    rejected: { label: "Rejeitado", color: "red" },
    processing: { label: "Processando", color: "purple" },
    returned: { label: "Devolvido", color: "orange" },
    cancelled: { label: "Cancelado", color: "red" },
    failed: { label: "Falhou", color: "red" },
    expired: { label: "Expirado", color: "gray" },
    refunded: { label: "Reembolsado", color: "orange" },
  };
  const key = String(status || "").toLowerCase();
  return map[key] || { label: status || "—", color: "gray" };
}

export function formatAccountInfo(
  agency?: string | null,
  account?: string | null,
  digit?: string | null
): string {
  if (!agency && !account) return "—";
  
  let result = "";
  if (agency) result += `Ag: ${agency}`;
  if (account) result += `${result ? " " : ""}C/C: ${account}`;
  if (digit) result += `-${digit}`;
  
  return result || "—";
}

export function formatTransactionType(type?: string): string {
  const typeMap: Record<string, string> = {
    pix: "PIX",
    ted: "TED",
    doc: "DOC",
    bank_slip: "Boleto",
    bank_transfer: "Transferência",
    payment: "Pagamento",
    refund: "Reembolso",
  };
  
  return typeMap[String(type || "").toLowerCase()] || type || "—";
}
